import { computed, effect, inject, Injectable, untracked } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { filter, map, take, tap } from "rxjs/operators";
import { updateStoredColor, updateStoredStrokeWidth } from "./toolbar-config-storage";
import { updateStoredToolbarPosition } from "./toolbar-config-session-storage";
import * as ToolbarActions from "./toolbar.actions";
import * as fromToolbar from "./toolbar.selectors";
import { ClassroomEvents } from "shared/classroom/data-access";
import { concatLatestFrom } from "@ngrx/operators";
import { ClassroomFacade } from "shared/classroom/data-access";
import { ToolbarUiStateService } from "./toolbar-ui-state.service";
import { SLIDE_SYNC_TOOLBAR_CONFIG } from "../injection-tokens";
import { PCP_LOGGER } from "@pcp/logging";
import { injectUiInteractionTracker } from "@pcp/insights";
import { FALLBACK_STROKE_COLOR, StrokeColor } from "shared/classroom/toolbar/core";

@Injectable()
export class ToolbarEffects {
    private readonly log = inject(PCP_LOGGER).createScoped('slide-sync');
    /** Emits the ETL-locked `UiInteraction` event; the name and logger scope are fixed inside. */
    private readonly trackUiInteraction = injectUiInteractionTracker();
    private readonly lessonState = inject(ClassroomFacade);

    // `optional: true` keeps unit tests and Storybook working when the effects are
    // registered without the app-level config providers.
    private readonly toolbarConfig = inject(SLIDE_SYNC_TOOLBAR_CONFIG, { optional: true });

    /** Resolved once: the configured default, or black if nobody configured one. */
    private readonly defaultStrokeColor = this.resolveDefaultStrokeColor();

    /**
     * Seeds the stroke color once at startup when neither localStorage nor state holds one.
     * Without it `ColorPaletteComponent` renders nothing, because its whole template is
     * wrapped in `@if (activatedColor$ | async)`.
     */
    readonly initStrokeColor$ = createEffect(() =>
        this.store.select(fromToolbar.selectStrokeColor).pipe(
            take(1),    //takes the very first value from the selector and then completes the stream
            filter((strokeColor) => !strokeColor),
            map(() =>
                ToolbarActions.strokeColorInitialized({
                    strokeColor: this.defaultStrokeColor,
                })
            ),
        )
    );

    /**
     * A missing `colorDefault` is a configuration mistake, not a runtime state: warn once
     * and keep the color palette usable instead of letting it render empty.
     */
    private resolveDefaultStrokeColor(): StrokeColor {
        const configured = this.toolbarConfig?.colorDefault;
        if (configured) {
            return configured;
        }

        this.log.warn(
            'SLIDE_SYNC_TOOLBAR_CONFIG.colorDefault is not configured - falling back to black',
            { fallback: FALLBACK_STROKE_COLOR, tokenProvided: !!this.toolbarConfig },
        );
        return FALLBACK_STROKE_COLOR;
    }

    /**
     * Resets all tools (drawing tools, color palette, bookpage) when a new
     * slide is shown on any layer.
     *
     * Stage D.7 (write-path refactor) split the original
     * `resetAllActivatedTools\$` into two pieces:
     *  - The slide-change trigger (this Signal effect): watches the Y.Doc
     *    layer-slide Signals; dispatches `deactivateAllTools` on change.
     *  - The bus-based triggers (`resetAllActivatedToolsOnBusEvent\$`
     *    below): `resetDynamicContent` (until D.8 collapses that flow) and
     *    `closeBookpageDialogCancel` (a user-action; legitimately on bus).
     */
    private wireDeactivateToolsOnSlideChange(): void {
        const slides = computed(() => ({
            p: this.lessonState.primarySlide(),
            s: this.lessonState.secondarySlide(),
            t: this.lessonState.tertiarySlide(),
        }));
        let initialized = false;
        effect(() => {
            slides(); // subscribe to the aggregate
            if (!initialized) {
                initialized = true;
                return;
            }
            untracked(() =>
                this.store.dispatch(ToolbarActions.deactivateAllTools()),
            );
        });
    }

    readonly resetAllActivatedToolsOnBusEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ClassroomEvents.userResetActiveSlide,
                ToolbarActions.closeBookpageDialogCancel,
            ),
            map(() => ToolbarActions.deactivateAllTools()),
        ),
    );

    readonly persistToolbarConfigToLocalStorageOnColorChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.strokeColorSelected),
                tap((action) => updateStoredColor(action.strokeColor))
            ),
        { dispatch: false }
    );

    readonly persistToolbarConfigToLocalStorageOnStrokeWidthChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.strokeWidthSelected),
                tap((action) => updateStoredStrokeWidth(action.strokeWidth))
            ),
        { dispatch: false }
    );

    readonly persistToolbarConfigToLocalStorageOnToolbarPositionChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.setToolbarPosition),
                tap((action) => updateStoredToolbarPosition(action.position))
            ),
        { dispatch: false }
    );


    /**
     * Bookpage dialog confirmed --> show the selected bookpage inside the preview screen.
     */
    readonly selectBookpageOnBookpageDialogConfirm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ToolbarActions.closeBookpageDialogConfirm),
            map((action) =>
                ToolbarActions.bookpageHasBeenSelected({
                    bookpage: action.selectedBookpage,
                })
            )
        )
    );

    /**
     * Bookpage dialog canceled --> remove any bookpage from the preview screen.
     */
    readonly removeSecondarySlideOnBookpageDialogCancel$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.closeBookpageDialogCancel),
                tap(() => this.lessonYjs.closeLayer(2))
            ),
        { dispatch: false }
    );

    readonly logEnableDisableDrawingTools$ = createEffect(() => this.actions$.pipe(
        ofType(ToolbarActions.toggleDrawingTool),
        tap(payload => this.trackUiInteraction('Toolbar', 'DrawingTool', payload.toolName, 'toggle')),
    ), { dispatch: false });

    readonly logToggleColorPalette$ = createEffect(() => this.actions$.pipe(
        ofType(ToolbarActions.toggleColorPalette),
        tap(() => this.trackUiInteraction('Toolbar', 'DrawingTool', 'ColorPalette', 'toggle')),
    ), { dispatch: false });

    readonly openStrokeWidth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ToolbarActions.toggleDrawingMenu
            ),
            concatLatestFrom(() => [
                this.store.select(fromToolbar.isDrawingMenuOpened)
            ]),
            filter(([action, opened]) => !opened),
            map(() => ToolbarActions.openDrawingMenu())
        )
    );
    
    
    readonly closeStrokeWidth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ToolbarActions.toggleDrawingMenu
            ),
            concatLatestFrom(() => [
                this.store.select(fromToolbar.isDrawingMenuOpened)
            ]),
            filter(([action, opened]) => opened),
            map(() => ToolbarActions.closeDrawingMenu())
        )
    );

    readonly logToggleBookpageTool$ = createEffect(() => this.actions$.pipe(
        ofType(ToolbarActions.toggleBookpageTool),
        tap(() => this.trackUiInteraction('Toolbar', 'Bookpage', '', 'toggle')),
    ), { dispatch: false });

    readonly logUndoRedoButtonClicked$ = createEffect(() => this.actions$.pipe(
        ofType(ToolbarActions.toolbarUndo, ToolbarActions.toolbarRedo),
        tap(action => {
            switch (action.type) {
                case '[Toolbar] undo button clicked':
                    this.trackUiInteraction('Toolbar', 'DrawingTool', 'UndoButton', 'toggle');
                    break;
                case '[Toolbar] redo button clicked':
                    this.trackUiInteraction('Toolbar', 'DrawingTool', 'RedoButton', 'toggle');
                    break;
            }
        }),
    ), { dispatch: false });

    readonly logColorSelected$ = createEffect(() => this.actions$.pipe(
        ofType(ToolbarActions.strokeColorSelected),
        tap(payload => this.trackUiInteraction('Toolbar', 'DrawingTool', 'ColorPalette', 'color selected', payload.strokeColor)),
    ), { dispatch: false });

    /**
     * Mark go-to-parent in-progress on click. Reset happens reactively inside
     * `ToolbarUiStateService` when any layer's slide changes or the
     * navigation-preview basic-slides Y.Doc field changes.
     */
    readonly trackGoToParentClicked$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.toolbarNavigateToParent),
                tap(() => this.toolbarUiState.markGoToParentClicked()),
            ),
        { dispatch: false },
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly lessonYjs: ClassroomFacade,
        private readonly toolbarUiState: ToolbarUiStateService,
    ) {
        this.wireDeactivateToolsOnSlideChange();
    }
}
